import { FC, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Avatar, Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Person, Work, CalendarToday, CorporateFare, Roofing } from '@mui/icons-material';

import { useEmployerAdminFindOneQuery } from '@carcareers/store';
import { EmployerAdminFindOneDto } from '@libs/dto';
import { SkeletonForm, EmployerUpdateForm, DashboardPageContainer } from '@carcareers/components';
import { FormProvider } from '@carcareers/contexts';
import { useIsMobile } from '@carcareers/utils';

export const EmployerView: FC = () => {
  const id = useParams().id;
  const isMobile = useIsMobile();

  const [employer, setEmployer] = useState<EmployerAdminFindOneDto>();
  const { data, isLoading } = useEmployerAdminFindOneQuery(id ?? '0', { skip: !id });

  useEffect(() => {
    if (!isLoading && data) {
      setEmployer(data);
    }
  }, [isLoading, data]);

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <DashboardPageContainer idReplacement={`${employer?.user?.firstName} ${employer?.user?.lastName}`}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {isLoading ? '' : getInitials(employer?.user?.firstName, employer?.user?.lastName)}
            </Avatar>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {isLoading ? (
                  <Skeleton variant="text" width={200} height={32} />
                ) : (
                  `${employer?.user?.firstName || ''} ${employer?.user?.lastName || ''}`
                )}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isLoading ? <Skeleton variant="text" width={150} height={20} /> : employer?.user?.email || ''}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {isLoading ? (
                  <Skeleton variant="rounded" width={200} height={32} />
                ) : (
                  <Chip
                    icon={<Person />}
                    label={`ID: ${employer?.user?.id || '...'}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={200} height={32} />
                ) : (
                  <Chip
                    icon={<CorporateFare />}
                    label={`Employer ID: ${employer?.id || '...'}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={200} height={32} />
                ) : (
                  <Chip
                    icon={<CalendarToday />}
                    label={`Joined ${new Date(employer?.createdAt || '').toLocaleDateString()}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Stack direction="row" spacing={2}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Work sx={{ color: 'primary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                employer?.activeJobsCount || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Active Jobs
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Roofing sx={{ color: 'success.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                employer?.ownedRooftopsCount || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Owned Rooftops
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'warning.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Roofing sx={{ color: 'primary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                employer?.authorizedRooftopsCount || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Authorized Rooftops
            </Typography>
          </CardContent>
        </Card>
      </Stack>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ color: 'primary.main' }} />
              Employer Details
            </Typography>
          </Box>
          <Box
            sx={{
              p: 3,
              '& form': {
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              },
            }}
          >
            <FormProvider initialData={employer}>{isLoading ? <SkeletonForm /> : <EmployerUpdateForm />}</FormProvider>
          </Box>
        </CardContent>
      </Card>
    </DashboardPageContainer>
  );
};

export default EmployerView;
